import { useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';

import { CommandContext } from '@components/features/CommandMenu/context';
import { Command } from '@components/features/CommandMenu/types';

export function useCommandMenu() {
  const { open, isOpen } = useContext(CommandContext);
  return useMemo(() => ({ open, isOpen }), [open, isOpen]);
}

export function useCommand(command: Command & { disabled?: boolean }) {
  const { register, unregister } = useContext(CommandContext);

  useEffect(() => {
    if (command.disabled) return;

    register(command);
    return () => {
      unregister(command);
    };
  }, [command, register, unregister]);
}

export function useCommandSearch(
  fetcher: (query: string) => Promise<Command[]>
) {
  const { query, startAsyncLoad, register, unregister } =
    useContext(CommandContext);

  const [commands, setCommands] = useState<Command[]>([]);
  useEffect(() => {
    commands.forEach(register);
    return () => {
      commands.forEach(unregister);
    };
  }, [commands, register, unregister]);

  useEffect(() => {
    if (!query) {
      setCommands([]);
      return;
    }

    const abortController = new AbortController();
    let timeout: NodeJS.Timeout | undefined;

    const stopAsyncLoad = startAsyncLoad();

    async function fetchResults() {
      // Debounce 200ms to avoid unnecessary API calls
      await new Promise((resolve) => {
        timeout = setTimeout(resolve, 300);
      });

      const results = await fetcher(query);

      if (abortController.signal.aborted) return;

      setCommands(results.map((c) => ({ ...c, filterable: false })));
      stopAsyncLoad();
    }

    void fetchResults();

    return () => {
      abortController.abort();
      clearTimeout(timeout);
      stopAsyncLoad();
    };
  }, [query, startAsyncLoad]);

  return commands;
}
